import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { cartAPI, wishlistAPI } from '../../config/api';
import resolveImageUrl from '../../utils/resolveImageUrl';
import {
  addDays,
  formatMoney,
  getDefaultDropoffDate,
  getTomorrowDate,
  toISODate,
} from '../../utils/rentalFields';
import { showAppToast } from '../../utils/notifications';
import { trackAddToCart } from '../../utils/analytics';
import swal from 'sweetalert';

function BookingStatusBadge({ status }) {
  const label = status || 'Available';
  const isAvailable = label.toLowerCase() === 'available';

  return (
    <span className={`detail-status ${isAvailable ? 'available' : 'reserved'}`}>
      {label}
    </span>
  );
}

const bookingRequirements = [
  {
    title: "Driver's licence",
    text: 'Must be valid and held for at least two years.',
    icon: 'bi-person-vcard',
  },
  {
    title: 'Minimum age',
    text: 'Primary drivers must be 25 or older.',
    icon: 'bi-calendar-check',
  },
  {
    title: 'Photo ID',
    text: 'National ID or passport on hand at pick-up.',
    icon: 'bi-passport',
  },
  {
    title: 'Proof of residence',
    text: 'A recent utility bill or official letter showing your current address.',
    icon: 'bi-house-check',
  },
  {
    title: 'Travel plans',
    text: 'Flight itinerary required when you are arriving from another city or country.',
    icon: 'bi-airplane',
  },
  {
    title: 'Deposit and rental charges',
    text: 'Security deposit and rental fees must be settled before confirmation.',
    icon: 'bi-credit-card',
  },
];

function ShowDetails({ products, userRole, onOpenAuthModal }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const product = products?.find((item) => String(item.id) === String(id));
  const minPickupDate = getTomorrowDate();

  const [pickupDate, setPickupDate] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [dropoffTime, setDropoffTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [requirementsAccepted, setRequirementsAccepted] = useState(false);

  const carouselImages = useMemo(() => {
    if (!product) return [];
    const images = [product.imageURL, ...(product.carousel_imgs || [])]
      .filter(Boolean)
      .map((image) => resolveImageUrl(image));
    return Array.from(new Set(images));
  }, [product]);

  useEffect(() => {
    if (carouselImages.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % carouselImages.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [carouselImages.length]);

  const dropoffMinDate = pickupDate ? getDefaultDropoffDate(pickupDate) : minPickupDate;
  const numberOfDays = useMemo(() => {
    if (!pickupDate || !dropoffDate) return 1;
    const diff = new Date(dropoffDate) - new Date(pickupDate);
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return Math.max(1, days || 1);
  }, [pickupDate, dropoffDate]);
  const totalCost = numberOfDays * Number(product?.price || 0);
  const features = product?.features?.length
    ? product.features
    : ['Chauffeur available', 'Airport pickup', 'Premium comfort'];
  const isUnavailable = product?.bookingStatus && product.bookingStatus.toLowerCase() !== 'available';
  const compareHref = product?.id ? `/vehicles/compare?compare=${product.id}` : '/vehicles/compare';
  const authNextPath = `${location.pathname}${location.search}`;
  const loginHref = `/auth?mode=login&next=${authNextPath}`;
  const signupHref = `/auth?mode=signup&next=${authNextPath}`;

  const handlePickupChange = (value) => {
    setPickupDate(value);
    if (dropoffDate && new Date(dropoffDate) <= new Date(value)) {
      setDropoffDate(toISODate(addDays(value, 1)));
    }
  };

  const validateBooking = () => {
    if (!pickupDate || !dropoffDate || !dropoffTime) {
      showAppToast({
        message: 'Please select pickup date, dropoff date, and dropoff time',
        type: 'warning',
      });
      return false;
    }

    if (new Date(dropoffDate) <= new Date(pickupDate)) {
      showAppToast({
        message: 'Dropoff date must be after pickup date',
        type: 'warning',
      });
      return false;
    }

    if (!requirementsAccepted) {
      showAppToast({
        message: 'Please confirm that you have read and can provide the required booking documents.',
        type: 'warning',
      });
      return false;
    }

    return true;
  };

  const handleAddToCart = async (event) => {
    event.preventDefault();

    if (!userRole) {
      navigate(loginHref);
      return;
    }

    if (!validateBooking()) return;

    setLoading(true);
    try {
      await cartAPI.add({
        productId: product.id,
        quantity: numberOfDays,
        bookedFrom: pickupDate,
        bookedFor: dropoffDate,
        dropoffTime,
      });
      trackAddToCart(product, numberOfDays);

      await swal({
        title: 'Confirm Booking',
        text: 'Your car has been added to cart. View your cart to confirm your booking.',
        icon: 'success',
        buttons: {
          stay: 'Close',
          cart: {
            text: 'View Cart',
            value: 'cart',
          },
        },
      }).then((choice) => {
        if (choice === 'cart') navigate('/cart');
      });
    } catch (error) {
      showAppToast({
        message: error.response?.data?.message || 'Failed to add booking',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToWishlist = async () => {
    if (!userRole) {
      navigate(loginHref);
      return;
    }

    setWishlistLoading(true);
    try {
      await wishlistAPI.add({ productId: product.id });
      showAppToast({
        message: 'Added to wishlist',
        type: 'success',
        duration: 2400,
      });
    } catch (error) {
      showAppToast({
        message: error.response?.data?.message || 'Failed to add to wishlist',
        type: 'error',
      });
    } finally {
      setWishlistLoading(false);
    }
  };

  if (!product) {
    return (
      <div className="booking-page px-4 py-16 text-center">
        <h2 className="section-title text-3xl text-primary-theme">Vehicle Not Found</h2>
        <p className="mt-3 text-secondary-theme">The vehicle you are looking for does not exist.</p>
        <button type="button" onClick={() => navigate('/vehicles')} className="btn-luxury mt-6">
          Browse All Vehicles
        </button>
      </div>
    );
  }

  return (
    <div className="booking-page rio-detail-page">
      <div className="detail-page-shell mx-auto">
        <nav className="rio-breadcrumb">
          <Link to="/home" className="rio-breadcrumb-link">
            <i className="bi bi-house-door" aria-hidden="true" />
            <span>Home</span>
          </Link>
          <i className="bi bi-chevron-right rio-breadcrumb-sep" aria-hidden="true" />
          <Link to="/vehicles" className="rio-breadcrumb-link">Vehicles</Link>
          <i className="bi bi-chevron-right rio-breadcrumb-sep" aria-hidden="true" />
          <span className="rio-breadcrumb-current">{product.name}</span>
        </nav>

        <section className="detail-top-grid rio-top-grid">
          {/* ── Left: Image Gallery Card ── */}
          <div className="detail-carousel rio-gallery-card">
            <button
              type="button"
              className="detail-carousel-main"
              onClick={() => setLightboxImage(carouselImages[activeSlide])}
              aria-label="Open vehicle image"
            >
              <img src={carouselImages[activeSlide]} alt={product.name} />
            </button>
            {carouselImages.length > 1 && (
              <>
                <button
                  type="button"
                  className="detail-carousel-control prev"
                  onClick={() => setActiveSlide((activeSlide - 1 + carouselImages.length) % carouselImages.length)}
                  aria-label="Previous image"
                >
                  <i className="bi bi-chevron-left" />
                </button>
                <button
                  type="button"
                  className="detail-carousel-control next"
                  onClick={() => setActiveSlide((activeSlide + 1) % carouselImages.length)}
                  aria-label="Next image"
                >
                  <i className="bi bi-chevron-right" />
                </button>
                <div className="detail-carousel-dots">
                  {carouselImages.map((image, index) => (
                    <button
                      key={image}
                      type="button"
                      className={index === activeSlide ? 'active' : ''}
                      onClick={() => setActiveSlide(index)}
                      aria-label={`Show image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Right: Info + Booking Panel ── */}
          <div className="detail-panel rio-info-panel">
            {/* Vehicle header */}
            <div className="rio-vehicle-header">
              <div className="rio-vehicle-title-group">
                <h1 className="rio-vehicle-name">{product.name}</h1>
                <p className="rio-vehicle-category">{product.categoryName || 'Luxury rental vehicle'}</p>
              </div>
              <p className="detail-price rio-price">
                {formatMoney(product.price)} <span>/ day</span>
              </p>
            </div>

            {/* Divider */}
            <div className="rio-divider" />

            {/* Description */}
            <div className="rio-description-block">
              <p className="rio-description">{product.description}</p>
              <button
                type="button"
                className="detail-more-toggle rio-view-more"
                onClick={() => setShowMoreDetails((value) => !value)}
              >
                {showMoreDetails ? 'Hide Details' : 'View More'}
                <i className={`bi ${showMoreDetails ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />
              </button>
            </div>

            {/* Expandable details grid */}
            {showMoreDetails && (
              <div className="detail-more-grid rio-specs-grid">
                <div className="rio-spec-card">
                  <i className="bi bi-tag" aria-hidden="true" />
                  <span>Category</span>
                  <strong>{product.categoryName || 'Luxury rental vehicle'}</strong>
                </div>
                <div className="rio-spec-card">
                  <i className="bi bi-calendar3" aria-hidden="true" />
                  <span>Model year</span>
                  <strong>{product.year || 'Premium fleet'}</strong>
                </div>
                <div className="rio-spec-card">
                  <i className="bi bi-cash-stack" aria-hidden="true" />
                  <span>Daily rate</span>
                  <strong>{formatMoney(product.price)}</strong>
                </div>
                <div className="rio-spec-card">
                  <i className="bi bi-check-circle" aria-hidden="true" />
                  <span>Availability</span>
                  <strong>{product.bookingStatus || 'Available'}</strong>
                </div>
              </div>
            )}

            {/* Status badge */}
            <div className="rio-status-row">
              <BookingStatusBadge status={product.bookingStatus} />
            </div>

            {/* Auth prompt for unauthenticated users */}
            {!userRole && (
              <aside className="detail-auth-prompt rio-auth-card" role="note">
                <div className="rio-auth-icon-wrap">
                  <i className="bi bi-shield-lock" aria-hidden="true" />
                </div>
                <div className="detail-auth-copy">
                  <strong>Sign in or create an account to continue booking.</strong>
                  <span>We use verified accounts to protect bookings, client identity, and vehicle handover details.</span>
                </div>
                <div className="detail-auth-actions">
                  <Link to={loginHref} className="btn-luxury">Sign In to Book</Link>
                  <Link to={signupHref} className="btn-luxury-outline">Create Account</Link>
                </div>
              </aside>
            )}

            {/* Divider */}
            <div className="rio-divider" />

            {/* Booking form section */}
            <div className="rio-booking-section">
              <h3 className="rio-section-label">
                <i className="bi bi-calendar-event" aria-hidden="true" />
                Reserve This Vehicle
              </h3>
              <form onSubmit={handleAddToCart} className="detail-booking-form rio-booking-form">
                <div className="rio-form-row">
                  <label>
                    <span>Pickup date</span>
                    <input
                      type="date"
                      min={minPickupDate}
                      value={pickupDate}
                      onChange={(event) => handlePickupChange(event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Dropoff date</span>
                    <input
                      type="date"
                      min={dropoffMinDate}
                      value={dropoffDate}
                      onChange={(event) => setDropoffDate(event.target.value)}
                      required
                    />
                  </label>
                </div>
                <div className="rio-form-row">
                  <label>
                    <span>Dropoff time</span>
                    <input
                      type="time"
                      value={dropoffTime}
                      onChange={(event) => setDropoffTime(event.target.value)}
                      required
                    />
                  </label>
                  <button
                    type="submit"
                    className="btn-luxury rio-book-btn"
                    disabled={loading || isUnavailable}
                  >
                    {isUnavailable ? 'Car booked' : loading ? 'Booking...' : 'Book Now'}
                  </button>
                </div>
              </form>

              <div className="detail-total rio-total-bar">
                <div className="rio-total-label">
                  <i className="bi bi-receipt" aria-hidden="true" />
                  <span>{numberOfDays} day{numberOfDays === 1 ? '' : 's'}</span>
                </div>
                <strong>{formatMoney(totalCost)}</strong>
              </div>
            </div>

            {/* Divider */}
            <div className="rio-divider" />

            {/* Features */}
            <div className="rio-features-section">
              <h3 className="rio-section-label">
                <i className="bi bi-stars" aria-hidden="true" />
                Features
              </h3>
              <ul className="features-list-inline rio-features">
                {features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div className="rio-actions-bar">
              <Link to={compareHref} className="btn-luxury rio-action-btn">
                <i className="bi bi-arrow-left-right" aria-hidden="true" />
                Compare This Vehicle
              </Link>
              <button
                type="button"
                className="btn-luxury-outline rio-action-btn"
                onClick={handleAddToWishlist}
                disabled={wishlistLoading}
              >
                <i className="bi bi-heart" aria-hidden="true" />
                {wishlistLoading ? 'Adding...' : 'Add To Wishlist'}
              </button>
              {userRole?.toLowerCase() === 'admin' && (
                <Link to={`/admin/vehicle/${product.id}`} className="btn-luxury-outline rio-action-btn">
                  <i className="bi bi-pencil-square" aria-hidden="true" />
                  Edit
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* ── Requirements Section ── */}
        <section className="requirements-section rio-requirements-section">
          <div className="rio-requirements-header">
            <div className="rio-requirements-icon-wrap">
              <i className="bi bi-clipboard-check" aria-hidden="true" />
            </div>
            <div className="requirements-heading-row">
              <span className="dashboard-mini-label rio-eyebrow">Rental readiness</span>
              <h2 className="requirements-heading rio-req-title">Before you book</h2>
              <p className="rio-req-subtitle">
                Please have the following ready. Documents must be current and match the details on your booking.
              </p>
            </div>
          </div>
          <div className="requirements-card-grid rio-req-grid">
            {bookingRequirements.map((requirement) => (
              <article key={requirement.title} className="requirements-card rio-req-card">
                <div className="rio-req-card-icon">
                  <i className={`bi ${requirement.icon}`} aria-hidden="true" />
                </div>
                <div className="rio-req-card-body">
                  <strong>{requirement.title}</strong>
                  <span>{requirement.text}</span>
                </div>
              </article>
            ))}
          </div>
          <label className="requirements-consent rio-consent">
            <input
              type="checkbox"
              checked={requirementsAccepted}
              onChange={(event) => setRequirementsAccepted(event.target.checked)}
            />
            <span>I confirm that I have read and can provide the required booking documents.</span>
          </label>
          <p className="requirements-note rio-note">
            Secure document uploads are not enabled for this booking step yet. DeRoyalty verifies original documents during pickup and account confirmation.
          </p>
        </section>
      </div>

      {lightboxImage && (
        <div className="detail-lightbox" onClick={() => setLightboxImage(null)} role="presentation">
          <button type="button" className="detail-lightbox-close" onClick={() => setLightboxImage(null)} aria-label="Close image">
            <i className="bi bi-x-lg" />
          </button>
          <div className="detail-lightbox-body" onClick={(event) => event.stopPropagation()} role="presentation">
            <img
              src={lightboxImage}
              alt={product.name}
              style={{ transform: `scale(${zoomLevel})` }}
            />
          </div>
          <div className="detail-zoom-controls">
            <button type="button" onClick={(event) => { event.stopPropagation(); setZoomLevel((current) => Math.min(3, current + 0.25)); }}>
              Zoom +
            </button>
            <button type="button" onClick={(event) => { event.stopPropagation(); setZoomLevel((current) => Math.max(1, current - 0.25)); }}>
              Zoom -
            </button>
            <button type="button" onClick={(event) => { event.stopPropagation(); setZoomLevel(1); }}>
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShowDetails;
